import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Game } from '../utils/gameUtils';
import { playPressSound } from '../utils/soundUtils';

interface GameModeProps {
  game: Game;
  currentGameMode: string | null;
  onGameModeChange: (modeId: string) => void;
  onRestart: () => void;
  startCooldown?: () => void;
  clearRestartCooldown?: () => void;
}

const GameMode: React.FC<GameModeProps> = ({
  game,
  currentGameMode,
  onGameModeChange,
  onRestart,
  startCooldown,
  clearRestartCooldown,
}) => {
  const modes = game?.info.gameModes || [];
  const modesPerRow = 2;

  const mobileRows = [];
  if (modes.length >= 4) {
    for (let i = 0; i < modes.length; i += modesPerRow) {
      mobileRows.push(modes.slice(i, i + modesPerRow));
    }
  }

  const renderButton = (mode) => (
    <motion.button
      key={mode.id}
      onClick={() => {
        playPressSound();
        if (clearRestartCooldown) {
          clearRestartCooldown();
        }
        onGameModeChange(mode.id);
        onRestart();
        if (startCooldown) {
          startCooldown();
        }
      }}
      className={`font-bold py-3 px-4 text-sm sm:text-base rounded-lg transition transform duration-300 flex-1 m-1 ${
        currentGameMode === mode.id
          ? 'bg-gradient-to-r from-yellow-500 to-red-500 text-white'
          : 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white hover:bg-gradient-to-r hover:from-yellow-500 hover:to-red-500 hover:text-white'
      }`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      {mode.name}
    </motion.button>
  );

  return (
    <AnimatePresence mode="wait">
      {game?.info.gameModes && game.info.gameModes.length > 0 && (
        <motion.div
          className="mt-4 mb-4 w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h4 className="text-base sm:text-lg font-semibold mb-3 text-gray-800 dark:text-white text-center">
            {game?.info.gameModeTitle || 'Selecciona el Modo'}
          </h4>
          {/* Móviles */}
          <motion.div
            className="block sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {modes.length <= 3 ? (
              <div className="flex justify-center space-x-2">
                {modes.map((mode) => renderButton(mode))}
              </div>
            ) : (
              <div className="flex flex-col space-y-2">
                {mobileRows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex justify-center space-x-2">
                    {row.map((mode) => renderButton(mode))}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
          {/* PC */}
          <motion.div
            className="hidden sm:flex justify-center space-x-3 flex-wrap"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {modes.map((mode) => renderButton(mode))}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GameMode;
